import loadable from "@loadable/component";
import { AnimatePresence } from "framer-motion";
import { debounce, get, isArray, isEmpty } from "lodash";
import { useReducer, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";

import { Error } from "@krea/common/shared-components/fields/error";
import Fieldset from "@krea/common/shared-components/fields/fieldset";
import { FileInput } from "@krea/common/shared-components/fields/file/index";
import FileItem from "@krea/common/shared-components/fields/file-item";
import FilesList from "@krea/common/shared-components/fields/files-list";
import TextArea from "@krea/common/shared-components/fields/textarea";
import {
  useDeleteFile,
  usePostFieldRequest,
} from "@krea/common/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/common/store/field-requests/hooks/queries";

import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const FreeTextParser = loadable(
  () => import("@krea/common/shared-components/freetext-parser"),
);

const AdditionalSecuritiesForm = ({
  fieldRequestID,
  isPreviewMode,
  isThreeStepsProcess,
  t,
  withInputLabel,
}) => {
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);

  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: deleteFile } = useDeleteFile();

  const files = submittedData?.files || [];
  const errors = error?.data?.errors || [];

  function reducer(state, action) {
    switch (action.type) {
      case "additional_securities_text":
        return { ...state, additional_securities_text: action.payload };
      case "touched":
        return { ...state, touched: action.payload };
      case "changeValue":
        return {
          ...state,
          additional_securities_text: action.payload,
          touched: state.additional_securities_text !== action.payload,
        };
      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    additional_securities_text: submittedData?.additional_securities_text || "",
    touched: false,
  });

  const { additional_securities_text } = state;

  const deleteFileFunc = (fileID) => {
    deleteFile({ fileID, fieldRequestID });
  };

  const onChange = ({ target }) => {
    const { value } = target;

    setState({ type: "changeValue", payload: value });
  };

  const createFormData = (value) => {
    const formData = new FormData();

    formData.append(
      "additional_securities_text",
      value.additional_securities_text || "",
    );

    if (isArray(value.files) && !isEmpty(value.files)) {
      value.files.forEach((file) => {
        formData.append("files", file, file.name);
      });
    }

    return formData;
  };

  const onBlur = (state, files = null) => {
    const { touched } = state;
    const value = state;

    value.files = isArray(files) ? files : null;
    const formData = createFormData(value);

    if (touched || isArray(files)) {
      postFieldRequest(formData);
      setState({ type: "touched", payload: false });
    }
  };

  const setCaretAtEnd = (e) => {
    const temp_value = e.target.value;
    e.target.value = "";
    e.target.value = temp_value;
  };

  const debouncedFunc = debounce((state) => onBlur(state), 2500);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {isPreviewMode ? (
        <>
          <FieldRequestPreviewHeader
            label={displayName}
            value={
              additional_securities_text && (
                <FreeTextParser text={additional_securities_text} />
              )
            }
          />

          {(additional_securities_text || (files && files.length > 0)) && (
            <>
              {files && files.length > 0 && (
                <FieldRequestPreviewHeader
                  subheader
                  label={t("iaq.uploadedFiles")}
                  value={
                    <>
                      <AnimatePresence>
                        {files.map((file) => (
                          <FileItem
                            file={file}
                            deleteFile={deleteFileFunc}
                            isPreviewMode={isPreviewMode}
                          />
                        ))}
                      </AnimatePresence>
                    </>
                  }
                />
              )}
            </>
          )}
        </>
      ) : (
        <>
          <form className="input-form" onSubmit={(e) => e.preventDefault()}>
            <Fieldset>
              <TextArea
                autoFocus={!isThreeStepsProcess}
                onFocus={setCaretAtEnd}
                id="additional-text"
                label={withInputLabel && displayName}
                placeholder={t("iaq.additionalSecurities.textareaPlaceholder")}
                value={additional_securities_text || ""}
                onChange={onChange}
                onBlur={() => onBlur(state)}
                error={get(errors, "[0].message", null)}
              />
              <div>
                <FileInput
                  multiple
                  label={t("iaq.additionalSecurities.attachFiles")}
                  files={files}
                  id="other-info"
                  postFiles={(e) => onBlur(state, e)}
                  error={get(errors, "property_files[0].message", null)}
                />
              </div>
              {files && files.length > 0 && (
                <FilesList label={t("iaq.uploadedFiles")}>
                  <AnimatePresence>
                    {files.map((file) => (
                      <FileItem
                        file={file}
                        deleteFile={deleteFileFunc}
                        isPreviewMode={isPreviewMode}
                      />
                    ))}
                  </AnimatePresence>
                </FilesList>
              )}
            </Fieldset>
          </form>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}

      {errors.map((error, index) => (
        <Error className="mt-3" key={index}>
          {error.message}
        </Error>
      ))}
    </>
  );
};

export default withTranslation()(AdditionalSecuritiesForm);
